import { onBeforeUnmount, type Ref, watch } from "vue";

// `run` never overlaps itself; the timer fires it every `intervalMs` (null = stopped) while the tab is visible.
export function usePolling(
  intervalMs: Ref<number | null>,
  poll: () => Promise<void>
) {
  let timer: ReturnType<typeof setInterval> | null = null;
  let running = false;

  async function run() {
    if (running) return;
    running = true;
    try {
      await poll();
    } finally {
      running = false;
    }
  }

  const runIfVisible = () => {
    if (document.visibilityState === "visible") run();
  };

  function stop() {
    if (timer) clearInterval(timer);
    timer = null;
  }

  // Coming back to the tab catches up at once instead of waiting out the interval.
  const onVisibilityChange = () => {
    if (intervalMs.value) runIfVisible();
  };

  watch(
    intervalMs,
    (ms) => {
      stop();
      if (ms) timer = setInterval(runIfVisible, ms);
    },
    { immediate: true }
  );

  document.addEventListener("visibilitychange", onVisibilityChange);
  onBeforeUnmount(() => {
    stop();
    document.removeEventListener("visibilitychange", onVisibilityChange);
  });

  return { run };
}
